import React, { useContext, useMemo, useState } from "react";
import { RCALibraryFiltersContext, initialState } from "../RCALibraryFiltersContext/RCALibraryFiltersContext";
import { IRCALibraryFiltersContextProps } from "../RCALibraryFiltersContext/RCALibraryFiltersContextInterfaces";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { UsersEntity } from "entities/Users/UsersEntity";
import { IGetUserFiltersPreferencesQuery } from "api/queries/usersAPI";
import { FiltersPreferencesReportsEnum } from "constants/constants";
import { Alert, Snackbar } from "@mui/material";
import { useTranslation } from "react-i18next";
import { filterObjectProperties, objectComparator } from "utilities/utilities";

export const valiRCALibraryFiltersDefaultFields = [
    "showCompleted",
    "millsIds",
    "selectedAllMillsIds",
    "meetingTypeIds",
    "totalMeetingTypes",
];

export const validRCALibraryExtraFiltersDefaultFields = [
    "searchText",
    "startDate",
    "endDate",
    "ownerId",
    "shiftId",
    "productionLineId",
    "machineCenterId",
    "functionalLocationId",
    "keySuccessFactorId",
    "failureTypeId",
];

export const RCALibraryFiltersContextProvider: React.FC<IRCALibraryFiltersContextProps> = ({ children }) => {
    const { t } = useTranslation();
    const { currentUser } = useContext(CorporateContext);

    const [filters, setFilters] = useState(initialState.filters);
    const [extraFilters, setExtraFilters] = useState(initialState.extraFilters);
    const [userFiltersDefault, setUserFiltersDefault] = useState<{
        filters: typeof initialState.filters;
        extraFilters: typeof initialState.extraFilters;
    } | null>(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const { upsertOperation, loading: loadingUpsertDefault } = UsersEntity.UpsertUserFiltersDefaultPreference();
    const { removeOperation, loading: loadingRemoveDefault } = UsersEntity.RemoveUserFiltersDefaultPreference();

    const handleQueryCompletion = (data: IGetUserFiltersPreferencesQuery) => {
        const userFilterPreference = data.user_filter_preferences;
        if (userFilterPreference[0]) {
            const filtersDefault = JSON.parse(userFilterPreference[0].filter_selection) as {
                filters: typeof initialState.filters;
                extraFilters: typeof initialState.extraFilters;
            };
            setUserFiltersDefault(filtersDefault);
            setFilters((prev) => ({ ...prev, ...filtersDefault.filters, ...filtersDefault.extraFilters }));
            setExtraFilters((prev) => ({ ...prev, ...filtersDefault.extraFilters }));
        } else {
            setUserFiltersDefault(null);
        }
    };
    const { loading: loadingGetDefault, refetch } = UsersEntity.GetUserFiltersPreferences(
        handleQueryCompletion,
        FiltersPreferencesReportsEnum.RCA_LIBRARY,
        currentUser?.user_id,
    );

    const handleFiltersChange = (value: typeof initialState.filters) => {
        setFilters(value);
    };

    const handleExtraFiltersChange = (value: typeof initialState.extraFilters) => {
        setExtraFilters(value);
    };

    const handleSetFiltersDefault = async (payload: {
        filters: typeof initialState.filters;
        extraFilters: typeof initialState.extraFilters;
    }) => {
        try {
            const validFilters = filterObjectProperties(payload.filters, valiRCALibraryFiltersDefaultFields);
            const validExtraFilters = filterObjectProperties(
                payload.extraFilters,
                validRCALibraryExtraFiltersDefaultFields,
            );

            const validatedFilters = {
                filters: validFilters,
                extraFilters: validExtraFilters,
            };

            await upsertOperation({
                variables: {
                    userPreference: {
                        user_id: currentUser?.user_id,
                        report_id: FiltersPreferencesReportsEnum.RCA_LIBRARY,
                        filter_selection: JSON.stringify(validatedFilters),
                    },
                },
            });
            await refetch();
            setSuccessMessage(t("locations.mill_dashboards.filters_saved"));
        } catch (error) {
            console.error(error);
            setErrorMessage(t("locations.mill_dashboards.filters_error"));
        }
    };

    const handleRemoveDefaultFilters = async () => {
        try {
            await removeOperation({
                variables: {
                    userId: currentUser?.user_id,
                    reportId: FiltersPreferencesReportsEnum.RCA_LIBRARY,
                },
            });
            setUserFiltersDefault(null);
            setSuccessMessage(t("locations.mill_dashboards.filters_removed"));
        } catch (error) {
            console.error(error);
            setErrorMessage(t("locations.mill_dashboards.filters_error"));
        }
    };

    const isFiltersDefault = useMemo(() => {
        if (userFiltersDefault) {
            return objectComparator(
                { ...userFiltersDefault?.filters, ...userFiltersDefault?.extraFilters },
                { ...filters, ...extraFilters },
                [...validRCALibraryExtraFiltersDefaultFields, ...valiRCALibraryFiltersDefaultFields],
            );
        }

        return false;
    }, [userFiltersDefault, filters, extraFilters]);

    const providerValue = {
        filters,
        extraFilters,
        handleFiltersChange,
        handleExtraFiltersChange,
        handleSetFiltersDefault,
        handleRemoveDefaultFilters,
        userFiltersDefault,
        loadingDefaultOperations: loadingGetDefault || loadingRemoveDefault || loadingUpsertDefault,
        isFiltersDefault,
    };

    return (
        <RCALibraryFiltersContext.Provider value={providerValue}>
            <Snackbar
                open={!!successMessage || !!errorMessage}
                autoHideDuration={2000}
                onClose={() => {
                    setErrorMessage("");
                    setSuccessMessage("");
                }}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert severity={successMessage ? "success" : "error"} sx={{ width: "100%" }}>
                    <span style={{ whiteSpace: "pre-line" }}>{successMessage || errorMessage}</span>
                </Alert>
            </Snackbar>
            {children}
        </RCALibraryFiltersContext.Provider>
    );
};
